import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CatalogCard from '@shared/components/CatalogCard/CatalogCard';
import { curriculumAPI, adaptiveAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

// Domains that write to the adaptive placement system
const ADAPTIVE_DOMAINS = new Set(['ai', 'ai_orchestrator', 'ai_entrepreneur', 'lsat', 'sat', 'ai_beginner']);

// θ thresholds → preference level (also saved to curriculum preference)
const thetaToLevel = (theta) => {
  if (theta >= 0.5) return 'advanced';
  if (theta >= -0.5) return 'intermediate';
  return 'beginner';
};

// task_type → human-readable topic label
const labelFromTaskType = (taskType = '') => {
  const cleaned = taskType
    .replace(/^[a-z_]+\./i, '')   // strip domain prefix e.g. "ai_orchestrator."
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const MAP = {
    'Gradient Descent': 'Gradient Descent & Optimization',
    'Neural Network Fundamentals': 'Neural Networks & Deep Learning',
    'Prompt Engineering': 'Prompt Engineering',
    'Tool Calling': 'Tool Calling & API Integration',
    'Agent Design': 'Multi-Agent System Design',
    'Model Selection': 'Model Selection & Evaluation',
    'Logical Reasoning': 'Logical Reasoning',
    'Logic Games': 'Analytical Reasoning (Logic Games)',
    'Reading Comprehension': 'Reading Comprehension',
    'Placement': 'General Knowledge Assessment',
  };
  return MAP[cleaned] || cleaned || 'General Knowledge Assessment';
};

// Level → θ label shown at completion
const THETA_LABELS = [
  { min: -Infinity, max: -0.8, emoji: '🌱', label: 'Beginner',     color: '#22c55e', desc: "You're starting fresh — great place to begin!" },
  { min: -0.8,      max:  0.2, emoji: '📘', label: 'Developing',   color: '#3b82f6', desc: 'You have foundational knowledge — ready to grow.' },
  { min:  0.2,      max:  0.8, emoji: '⚡', label: 'Intermediate',  color: '#eab308', desc: 'Solid foundation — time to go deeper.' },
  { min:  0.8,      max: Infinity, emoji: '🚀', label: 'Advanced',  color: '#ef4444', desc: "You're already strong — we'll push your limits." },
];

const getThetaLabel = (theta) =>
  THETA_LABELS.find((t) => theta >= t.min && theta < t.max) || THETA_LABELS[1];

// ─── Domain level cards ────────────────────────────────────────────────────────

const AI_LEVEL_CARDS = [
  { id: 'beginner', emoji: '🟢', title: 'Foundations', subtitle: 'Conceptual — no code required',
    description: "Built for learners new to AI systems. You'll explore core concepts, how AI works, and when to apply it — all without writing code.",
    content: 'Explainer videos, guided walkthroughs, and conceptual quizzes.', hours: '~4 hrs', color: '#22c55e' },
  { id: 'intermediate', emoji: '🟡', title: 'Practitioner', subtitle: 'For working builders',
    description: "Designed for builders applying AI in real products and workflows. You'll go deeper on patterns, tools, and production tradeoffs.",
    content: 'Applied labs, system design scenarios, and tool integration exercises.', hours: '~8 hrs', color: '#eab308' },
  { id: 'advanced', emoji: '🔴', title: 'Architect', subtitle: 'Production systems at scale',
    description: "For senior builders and operators owning AI systems. You'll tackle multi-agent pipelines, reliability, cost, and governance at scale.",
    content: 'Architecture reviews, incident simulations, and design critiques.', hours: '~12 hrs', color: '#ef4444' },
];

const DOMAIN_CARDS = {
  ai: AI_LEVEL_CARDS,
  ai_orchestrator: AI_LEVEL_CARDS,
  ai_beginner: AI_LEVEL_CARDS,
  ai_entrepreneur: [
    { id: 'beginner', emoji: '🟢', title: 'AI Starter', subtitle: 'First-time AI users',
      description: "Perfect if you're exploring AI tools for the first time. You'll discover practical ways to use AI in your work without any technical background.",
      content: 'Tool demos, quick-win workflows, and no-code use cases.', hours: '~3 hrs', color: '#22c55e' },
    { id: 'intermediate', emoji: '🟡', title: 'AI Builder', subtitle: 'Active AI users building products',
      description: "Built for founders and operators already using AI who want to build more deliberately. You'll go deeper on product strategy, prompting, and automation.",
      content: 'Strategy frameworks, prompt engineering, and workflow automation.', hours: '~6 hrs', color: '#eab308' },
    { id: 'advanced', emoji: '🔴', title: 'AI Operator', subtitle: 'Scaling AI-first companies',
      description: "For operators and founders scaling AI-powered businesses. You'll cover hiring for AI roles, building AI culture, and managing AI risk at company scale.",
      content: 'Org design, vendor evaluation, risk frameworks, and case studies.', hours: '~10 hrs', color: '#ef4444' },
  ],
  lsat: [
    { id: 'beginner', emoji: '🟢', title: 'Introduction', subtitle: 'New to the LSAT',
      description: "Start from scratch. Learn LSAT structure, core reasoning patterns, and the logic behind question types — no prior preparation assumed.",
      content: 'Core concept walkthroughs, worked examples, strategy foundations.', hours: '~6 hrs', color: '#22c55e' },
    { id: 'intermediate', emoji: '🟡', title: 'Practitioner', subtitle: 'Already studying for the LSAT',
      description: "You've covered the basics and need to sharpen your timing, accuracy, and harder question types under test conditions.",
      content: 'Timed drills, harder question sets, section strategy.', hours: '~10 hrs', color: '#eab308' },
    { id: 'advanced', emoji: '🔴', title: 'Expert', subtitle: 'Targeting 170+',
      description: "You score consistently above 165 and want to squeeze out every point. Focus on the hardest LR and RC patterns.",
      content: 'High-difficulty banks, inference chains, near-perfect accuracy drills.', hours: '~14 hrs', color: '#ef4444' },
  ],
};

const GENERIC_CARDS = [
  { id: 'beginner', emoji: '🟢', title: 'Beginner', subtitle: 'New to this domain',
    description: "Start from the basics. You'll build foundational knowledge at a steady pace with plenty of guided practice.",
    content: 'Core concepts, guided examples, and introductory exercises.', hours: '~4 hrs', color: '#22c55e' },
  { id: 'intermediate', emoji: '🟡', title: 'Intermediate', subtitle: 'Some prior experience',
    description: "You have the basics down and are ready to go deeper. Content assumes familiarity and moves at a faster pace.",
    content: 'Applied scenarios, moderate challenges, and skill-building drills.', hours: '~7 hrs', color: '#eab308' },
  { id: 'advanced', emoji: '🔴', title: 'Advanced', subtitle: 'Experienced practitioner',
    description: "You're already proficient and want to push your limits. Expect complex, high-pressure scenarios and expert-level content.",
    content: 'Edge cases, leadership decisions, and mastery-level assessments.', hours: '~12 hrs', color: '#ef4444' },
];

// ─── Main component ────────────────────────────────────────────────────────────

export default function DifficultyPickerPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { learner } = useAuth();
  const trackParam = searchParams.get('track');
  const domainReturnPath = trackParam
    ? `/domain/${domain}?track=${encodeURIComponent(trackParam)}`
    : `/domain/${domain}`;

  // Views: 'choose' | 'pick' | 'cat' | 'cat_result'
  const [view, setView] = useState('choose');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // CAT session state
  const [catSession, setCatSession]     = useState(null);   // {session_id, theta, …}
  const [catItem, setCatItem]           = useState(null);   // current item
  const [catCount, setCatCount]         = useState(0);      // items answered
  const [catEstimate, setCatEstimate]   = useState(8);      // items remaining estimate
  const [catResponses, setCatResponses] = useState([]);     // [{item_id, correct}]
  const [catTheta, setCatTheta]         = useState(0);
  const [catLoading, setCatLoading]     = useState(false);
  const startedAt = useRef(null);

  const cards = DOMAIN_CARDS[domain] || GENERIC_CARDS;
  const domainLabel = domain === 'ai'
    ? 'AI'
    : domain
      ? domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : '';
  const isAdaptive = ADAPTIVE_DOMAINS.has(domain);

  // Pre-select existing preference
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await curriculumAPI.getPreference(domain);
        if (!cancelled && data.difficulty) setSelected(data.difficulty);
      } catch { /* no preference yet */ }
    })();
    return () => { cancelled = true; };
  }, [domain]);

  // ── Path A: Level picker ────────────────────────────────────────────────────

  const handlePickContinue = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await curriculumAPI.setPreference(domain, selected);
      // Seed adaptive mastery records from level selection (fixes previous no-op)
      if (isAdaptive && learner?.learner_id) {
        await adaptiveAPI.setPlacement({
          learner_id: learner.learner_id,
          domain,
          level_selection: selected,
        }).catch(() => {}); // non-fatal
      }
      navigate(domainReturnPath);
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await curriculumAPI.setPreference(domain, 'intermediate');
      if (isAdaptive && learner?.learner_id) {
        adaptiveAPI.setPlacement({
          learner_id: learner.learner_id,
          domain,
          level_selection: 'intermediate',
        }).catch(() => {});
      }
    } catch { /* non-fatal */ }
    navigate(domainReturnPath);
  };

  // ── Path B: CAT adaptive probe ──────────────────────────────────────────────

  const startCat = async () => {
    if (!learner?.learner_id) { setError('Not logged in.'); return; }
    setCatLoading(true);
    setError(null);
    startedAt.current = Date.now();
    try {
      const { data } = await adaptiveAPI.startCatSession({
        learner_id: learner.learner_id,
        skill_id: 'placement',
        domain,
        session_type: 'adaptive',
      });
      setCatSession(data);
      setCatItem(data.first_item);
      setCatEstimate(data.items_remaining_estimate || 8);
      setView('cat');
    } catch (e) {
      setError('Could not start assessment. Try picking your level instead.');
    } finally {
      setCatLoading(false);
    }
  };

  const handleCatAnswer = async (correct) => {
    if (!catSession || !catItem || catLoading) return;
    setCatLoading(true);
    const timeTaken = startedAt.current ? Date.now() - startedAt.current : 0;
    const newResponses = [...catResponses, { item_id: catItem.item_id, correct, is_correct: correct }];
    setCatResponses(newResponses);
    startedAt.current = Date.now();
    try {
      const { data } = await adaptiveAPI.respondCatSession(catSession.session_id, {
        item_id: catItem.item_id,
        correct,
        time_taken_ms: timeTaken,
      });
      const newCount = catCount + 1;
      setCatCount(newCount);
      setCatTheta(data.theta ?? 0);

      if (data.session_complete) {
        const finalTheta = data.theta ?? 0;
        // Seed mastery via diagnostic-complete
        try {
          await adaptiveAPI.completeDiagnostic({
            learner_id: learner.learner_id,
            domain,
            cat_theta: finalTheta,
            item_responses: newResponses,
          });
        } catch { /* non-fatal */ }
        // Also save a curriculum preference so DomainPage doesn't redirect again
        const level = thetaToLevel(finalTheta);
        try {
          await curriculumAPI.setPreference(domain, level);
        } catch { /* non-fatal */ }
        setView('cat_result');
      } else {
        setCatItem(data.next_item);
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setCatLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>

      {/* ── Choose method ──────────────────────────────────────────────────── */}
      {view === 'choose' && (
        <div style={S.section}>
          <div style={S.header}>
            <h1 style={S.title}>Get placed in <strong style={{ color: '#a78bfa' }}>{domainLabel}</strong></h1>
            <p style={S.subtitle}>How would you like to set your starting level?</p>
          </div>

          <div style={S.methodGrid}>
            {/* Option A — Quick pick */}
            <button
              style={S.methodCard}
              data-testid="placement-pick-level"
              onClick={() => setView('pick')}
            >
              <div style={S.methodIcon}>🎯</div>
              <div style={S.methodTitle}>I know my level</div>
              <div style={S.methodDesc}>
                Pick Beginner, Intermediate, or Advanced and jump straight in.
              </div>
              <div style={S.methodMeta}>~30 seconds</div>
              <div style={{ ...S.methodBadge, background: 'rgba(99,102,241,0.15)', color: '#a78bfa', borderColor: 'rgba(99,102,241,0.3)' }}>
                Quick Start
              </div>
            </button>

            {/* Option B — Adaptive probe */}
            <button
              style={S.methodCard}
              data-testid="placement-take-test"
              onClick={startCat}
              disabled={catLoading}
            >
              <div style={S.methodIcon}>{catLoading ? '⏳' : '📊'}</div>
              <div style={S.methodTitle}>{catLoading ? 'Starting…' : 'Test my knowledge'}</div>
              <div style={S.methodDesc}>
                Answer 5–10 adaptive questions. We'll place you precisely and personalise your content order.
              </div>
              <div style={S.methodMeta}>~4 minutes · More accurate</div>
              <div style={{ ...S.methodBadge, background: 'rgba(245,158,11,0.12)', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.35)' }}>
                Recommended
              </div>
            </button>
          </div>

          {error && <p style={S.errorText}>{error}</p>}

          <button style={S.skipLink} onClick={handleSkip}>
            Skip for now (defaults to Intermediate)
          </button>
        </div>
      )}

      {/* ── Level picker ───────────────────────────────────────────────────── */}
      {view === 'pick' && (
        <div style={S.section}>
          <button style={S.backBtn} onClick={() => setView('choose')}>← Back</button>
          <div style={S.header}>
            <h1 style={S.title}>Pick your learning level</h1>
            <p style={S.subtitle}>for <strong>{domainLabel}</strong></p>
            <p style={S.hint}>You can adjust this anytime from the domain page.</p>
          </div>

          <div style={S.cardRow}>
            {cards.map((card) => (
              <CatalogCard
                key={card.id}
                id={card.id}
                icon={card.emoji}
                title={card.title}
                description={`${card.subtitle}\n\n${card.description}\n\n${card.content}`}
                badges={[{ label: card.hours, color: card.color }]}
                selectable
                selected={selected === card.id}
                onSelect={setSelected}
                selectedBorderColor={card.color}
              />
            ))}
          </div>

          {error && <p style={S.errorText}>{error}</p>}

          <div style={S.actions}>
            <button
              className="btn-primary"
              style={{ ...S.continueBtn, opacity: selected ? 1 : 0.45, cursor: selected ? 'pointer' : 'not-allowed' }}
              disabled={!selected || saving}
              onClick={handlePickContinue}
            >
              {saving ? 'Saving…' : 'Continue →'}
            </button>
            <button style={S.skipLink} onClick={handleSkip}>
              Skip for now
            </button>
          </div>
        </div>
      )}

      {/* ── CAT adaptive probe ─────────────────────────────────────────────── */}
      {view === 'cat' && catItem && (
        <div style={S.section}>
          <div style={S.catHeader}>
            <div style={S.catProgress}>
              <div style={S.catProgressBar}>
                <div
                  style={{
                    ...S.catProgressFill,
                    width: `${Math.min(100, (catCount / Math.max(catEstimate, catCount + 1)) * 100)}%`,
                  }}
                />
              </div>
              <span style={S.catProgressLabel}>
                Question {catCount + 1} of ~{Math.max(catEstimate, catCount + 1)}
              </span>
            </div>
            <div style={S.thetaIndicator}>
              Estimated level: <strong>{getThetaLabel(catTheta).label}</strong>
            </div>
          </div>

          <div style={S.catCard}>
            {catItem.answer_choices && catItem.answer_choices.length > 0 ? (
              /* ── MCQ mode: real question from item_bank ───────────────── */
              <>
                {catItem.skill_id && (
                  <div style={S.catSkillTag}>
                    {labelFromTaskType(catItem.skill_id || catItem.task_type)}
                  </div>
                )}
                <div style={S.catQuestion} data-testid="cat-question-text">
                  {catItem.question_text}
                </div>
                <div style={S.mcqChoices}>
                  {catItem.answer_choices.map((choice, idx) => (
                    <button
                      key={idx}
                      style={S.mcqChoice}
                      disabled={catLoading}
                      data-testid={`cat-choice-${idx}`}
                      onClick={() => handleCatAnswer(true)}
                    >
                      {choice}
                    </button>
                  ))}
                  <button
                    style={{ ...S.mcqChoice, ...S.mcqChoiceDontKnow }}
                    disabled={catLoading}
                    data-testid="cat-dont-know"
                    onClick={() => handleCatAnswer(false)}
                  >
                    I don't know this one
                  </button>
                </div>
                <p style={S.catTopicNote}>
                  Select your answer — or click "I don't know" to skip.
                </p>
              </>
            ) : (
              /* ── Self-assessment fallback: topic label ─────────────────── */
              <>
                <div style={S.catQuestion}>
                  Do you feel confident with this topic?
                </div>
                <div style={S.catTopic} data-testid="cat-topic-label">
                  {labelFromTaskType(catItem.task_type || catItem.question_text)}
                </div>
                <p style={S.catTopicNote}>
                  Answer honestly — this calibrates your starting point, not your score.
                </p>
                <div style={S.catButtons}>
                  <button
                    style={{ ...S.catBtn, ...S.catBtnYes }}
                    disabled={catLoading}
                    data-testid="cat-yes"
                    onClick={() => handleCatAnswer(true)}
                  >
                    <span style={{ fontSize: 22 }}>✓</span>
                    <span>Yes, I know this</span>
                  </button>
                  <button
                    style={{ ...S.catBtn, ...S.catBtnNo }}
                    disabled={catLoading}
                    data-testid="cat-no"
                    onClick={() => handleCatAnswer(false)}
                  >
                    <span style={{ fontSize: 22 }}>✗</span>
                    <span>Still learning</span>
                  </button>
                </div>
              </>
            )}
            {catLoading && <p style={S.catLoadingNote}>Loading next question…</p>}
          </div>

          <button style={S.skipLink} onClick={handleSkip}>
            Skip assessment
          </button>
        </div>
      )}

      {/* ── CAT result ─────────────────────────────────────────────────────── */}
      {view === 'cat_result' && (() => {
        const lbl = getThetaLabel(catTheta);
        return (
          <div style={{ ...S.section, textAlign: 'center', maxWidth: 520 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>{lbl.emoji}</div>
            <h1 style={{ ...S.title, color: lbl.color }}>
              {lbl.label}
            </h1>
            <p style={{ ...S.subtitle, marginBottom: 8 }}>{lbl.desc}</p>
            <p style={{ fontSize: 13, opacity: 0.5, marginBottom: 32 }}>
              Based on {catCount} questions • Confidence score: {Math.round((1 / (1 + Math.exp(-catTheta))) * 100)}%
            </p>
            <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 32 }}>
              Your curriculum and review card order will reflect this placement. You can retake this from the domain settings anytime.
            </p>
            <button
              className="btn-primary"
              style={S.continueBtn}
              onClick={() => navigate(domainReturnPath)}
            >
              Start Learning →
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
    color: '#e2e8f0',
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  section: {
    width: '100%',
    maxWidth: 820,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 32,
    position: 'relative',
  },
  header: { textAlign: 'center' },
  title: { fontSize: '2rem', fontWeight: 700, margin: 0 },
  subtitle: { fontSize: '1.1rem', marginTop: 10, opacity: 0.8 },
  hint: { fontSize: '0.85rem', opacity: 0.5, marginTop: 4 },

  // Method choice cards
  methodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
    width: '100%',
  },
  methodCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '2px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '28px 24px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    textAlign: 'left',
    color: '#e2e8f0',
    transition: 'border-color 0.2s, background 0.2s',
    position: 'relative',
  },
  methodIcon: { fontSize: 36 },
  methodTitle: { fontSize: '1.15rem', fontWeight: 700 },
  methodDesc: { fontSize: '0.875rem', opacity: 0.7, lineHeight: 1.5 },
  methodMeta: { fontSize: '0.78rem', opacity: 0.5, marginTop: 4 },
  methodBadge: {
    fontSize: '0.72rem',
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 99,
    border: '1px solid',
    marginTop: 4,
    letterSpacing: '0.03em',
  },

  // Level picker
  cardRow: {
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
  },
  actions: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  continueBtn: {
    padding: '12px 36px',
    fontSize: '1rem',
    fontWeight: 700,
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff',
    cursor: 'pointer',
  },

  // CAT
  catHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  catProgress: { display: 'flex', alignItems: 'center', gap: 10 },
  catProgressBar: { width: 160, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' },
  catProgressFill: { height: '100%', background: 'linear-gradient(90deg, #6366f1, #a78bfa)', borderRadius: 99, transition: 'width 0.4s' },
  catProgressLabel: { fontSize: 13, opacity: 0.6 },
  thetaIndicator: { fontSize: 13, opacity: 0.6 },
  catCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 560,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    textAlign: 'center',
  },
  catQuestion: { fontSize: '1.05rem', opacity: 0.65, fontWeight: 500 },
  catTopic: {
    fontSize: '1.45rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#c4b5fd',
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  catTopicNote: { fontSize: '0.8rem', opacity: 0.45, maxWidth: 380, lineHeight: 1.5 },
  catButtons: { display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' },
  catBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '18px 32px',
    borderRadius: 14,
    border: '2px solid',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 700,
    minWidth: 140,
    transition: 'opacity 0.15s, transform 0.1s',
  },
  catBtnYes: { background: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.4)', color: '#4ade80' },
  catBtnNo:  { background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.35)', color: '#f87171' },
  catLoadingNote: { fontSize: 13, opacity: 0.5 },

  catSkillTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#a78bfa',
    opacity: 0.8,
  },
  mcqChoices: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    marginTop: 4,
  },
  mcqChoice: {
    textAlign: 'left',
    padding: '12px 18px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
    lineHeight: 1.4,
  },
  mcqChoiceDontKnow: {
    marginTop: 4,
    background: 'rgba(239,68,68,0.06)',
    borderColor: 'rgba(239,68,68,0.2)',
    color: '#f87171',
    fontSize: '0.82rem',
    textAlign: 'center',
  },

  backBtn: {
    alignSelf: 'flex-start',
    background: 'none',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#94a3b8',
    padding: '6px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
  },
  skipLink: {
    fontSize: '0.875rem',
    opacity: 0.5,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: 'inherit',
    padding: 0,
  },
  errorText: { color: '#f87171', fontSize: '0.875rem' },
};
